import React, { useEffect, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import { formatDateTime } from './esgEvaluationUi';

const labelStyle = { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' };

const yesNo = (v) => (v === null || v === undefined ? '—' : v ? 'Yes' : 'No');

function Field({ label, children }) {
  return (
    <div className="form-field-group">
      <label>{label}</label>
      <div className="esg-introduction-view__div-1">
        {children || <span className="esg-introduction-view__span-2">Not logged</span>}
      </div>
    </div>
  );
}

// The Introduction council (council 0): nothing to evaluate or edit here, just what the squad logged in LMS -
// the date, minutes, attendance, who introduced themselves, the squad profile, chairs and members' own ratings.
export default function EsgIntroductionView({ squadId, onBack, onError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    mentorApi.getEsgIntroduction(squadId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) onError?.("Couldn't load the Introduction - try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [squadId, onError]);

  const backButton = <button type="button" className="btn-back-link" onClick={onBack}>← Back to squad</button>;

  if (loading) {
    return <div className="evaluation-workspace animate-fade-in esg-introduction-view__div-3">{backButton}<p className="esg-introduction-view__span-2">Loading Introduction...</p></div>;
  }
  if (!data) {
    return (
      <div className="evaluation-workspace animate-fade-in esg-introduction-view__div-3">
        {backButton}
        <div className="empty-state-card"><p className="empty-state-description">The Introduction could not be loaded.</p></div>
      </div>
    );
  }

  const context = [
    ['Date', data.session.scheduled_at ? data.session.scheduled_at.split('T')[0] : '—'],
    ['Time', data.session.scheduled_at ? new Date(data.session.scheduled_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '—'],
    ['Squad', data.squad.name],
    ['Level', data.squad.level?.sequence ? `Level ${data.squad.level.sequence}` : '—'],
    ['Council 1 scheduled', data.next_session.scheduled_at ? formatDateTime(data.next_session.scheduled_at) : 'Not scheduled'],
  ];

  return (
    <div className="evaluation-workspace animate-fade-in esg-introduction-view__div-4">
      {backButton}

      <div className="evaluation-sticky-header">
        <div className="sticky-header-container">
          <div className="sticky-header-meta">
            <h2 className="sticky-title">Introduction Council</h2>
            <p className="sticky-subtitle">{`${data.squad.name} • Read only`}</p>
          </div>
          <span className="status-badge status-planned">View only</span>
        </div>
      </div>

      <div className="esg-introduction-view__div-5">
        {context.map(([label, value]) => (
          <div key={label}>
            <div style={labelStyle}>{label}</div>
            <div className="esg-introduction-view__div-6">{value}</div>
          </div>
        ))}
      </div>

      <div className="form-section-header"><h3>Session Record</h3><div className="section-divider"></div></div>
      <div className="form-card-container">
        <Field label="Summary">{data.report?.summary}</Field>
        <Field label="Minutes">{data.report?.minutes}</Field>
      </div>

      <div className="form-section-header esg-introduction-view__div-7"><h3>Squad Profile</h3><div className="section-divider"></div></div>
      <div className="form-card-container">
        <Field label="Squad name chosen by the admin lead">{data.profile.squad_name}</Field>
        <Field label="Ground rules">{data.profile.ground_rules}</Field>
      </div>

      <div className="form-section-header esg-introduction-view__div-7"><h3>Attendance, Introductions &amp; Ratings</h3><div className="section-divider"></div></div>
      <div className="students-table-card">
        <table className="students-table">
          <thead>
            <tr><th>Member</th><th>Present</th><th>Introduced themselves</th><th>Self rating</th><th>Learning</th></tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.student_id}>
                <td>{m.name}</td>
                <td>{yesNo(m.present)}</td>
                <td>{yesNo(m.introduced)}</td>
                <td>{m.self_rating ?? '-'}</td>
                <td>{m.self_learning || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="form-section-header esg-introduction-view__div-7"><h3>Chair Allocation</h3><div className="section-divider"></div></div>
      <div className="students-table-card">
        <table className="students-table">
          <thead><tr><th>Mission</th><th>Chair</th></tr></thead>
          <tbody>
            {data.chairs.length === 0 && <tr><td colSpan={2} className="esg-introduction-view__td-8">No chairs allocated yet.</td></tr>}
            {data.chairs.map((c) => (
              <tr key={c.mission_number}><td>{`Mission ${c.mission_number}`}</td><td>{c.name || '—'}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
